import { callFirebaseFunction } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import { network } from 'boot/network';
import {
  chain, compact, find, get, includes, isEmpty, isObject, map, omit,
} from 'lodash';
import { notifyNegative } from 'src/composables/interactions';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import DataDB from 'src/fb/data-db';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import SessionGroupsDB from 'src/fb/sessionGroups-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import VideoSessionsDB from 'src/fb/videoSessions-db';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { programs as useProgramsStore } from 'src/store/programs';
import { programsTargets as useProgramsTargetsStore } from 'src/store/programsTargets';
import { userRoles } from 'src/utils/AppUtils';
import {
  bindDataBaseStore,
  bindDataBaseStoreByArrayOperator,
  unbindDataBaseStore,
} from 'src/utils/FirestoreUtils';
import { logEvent } from 'src/utils/LogEvent';

export default {

  setScheduledSessions(scheduledSessions) {
    if (network.offlineMode) {
      this.scheduledSessions = scheduledSessions.filter((session) => !session.sessionGroup);
      return;
    }

    this.scheduledSessions = scheduledSessions;
  }, // setScheduledSessions()

  addActiveSessionProgramMod({ sessionProgram, programModNotes }) {
    const sessionProgramId = sessionProgram?.id;
    if (!sessionProgramId) {
      return;
    }
    if (chain(this.activeSessionProgramMods).get(sessionProgramId).isEmpty().value()) {
      this.activeSessionProgramMods[sessionProgramId] = [];
    }
    this.activeSessionProgramMods[sessionProgramId].push(programModNotes);
  }, // addActiveSessionProgramMod()

  resetActiveSessionProgramModifications() {
    this.activeSessionProgramMods = {};
  }, // resetActiveSessionProgramModifications()

  setLoadedActiveSessionPrograms(sessionPrograms) {
    this.loadedActiveSessionPrograms = sessionPrograms;
  }, // setLoadedActiveSessionPrograms()

  setActiveSessionIdPointer(sessionId) {
    this.activeSessionIdPointer = sessionId;
  }, // setActiveSessionIdPointer()

  setGroupSessionPrograms({ sessionId, sessionPrograms }) {
    this.groupSessionPrograms[sessionId] = sessionPrograms;
  }, // setGroupSessionPrograms()

  setActiveSessionProgramsTargetStatusSelected(payload) {
    const activeSessionProgram = find(
      this.loadedActiveSessionPrograms,
      (sessionProgram) => sessionProgram === payload.sessionProgram,
    );
    if (activeSessionProgram) {
      activeSessionProgram.selectedTargets[payload.targetStatus] = payload.value;
    }
  }, // setActiveSessionProgramsTargetStatusSelected()

  updateTargetPropertiesInTarget({ sessionProgram, target, targetProperties }) {
    const targetObj = chain(this.loadedActiveSessionPrograms)
      .find({ id: sessionProgram.id })
      .get(['program', 'targets'])
      .find({ id: target.id })
      .value();

    if (!isObject(targetObj)) {
      return;
    }
    Object.assign(targetObj, targetProperties);
  }, // updateTargetPropertiesInTarget()

  setVideoSessionMembersNames(payload = {}) {
    this.videoSessionMembersNames = payload;
  }, // setVideoSessionMembersNames()

  /**
   * Create bind to an active session
   */
  bindActiveSession({ sessionId }) {
    const sessionsDb = new SessionsDB();
    const sessionRef = sessionsDb.getDocRef(sessionId);
    return bindDataBaseStore({ store: this, property: 'primaryActiveSession' }, sessionRef, {});
  }, // bindActiveSession()

  unbindActiveSession() {
    return unbindDataBaseStore({ store: this, property: 'primaryActiveSession' }, {});
  }, // unbindActiveSession()

  bindActiveSessionGroup({ sessionGroupId }) {
    const sessionGroupsDb = new SessionGroupsDB();
    const sessionGroupRef = sessionGroupsDb.getDocRef(sessionGroupId);
    return bindDataBaseStore({ store: this, property: 'activeSessionGroup' }, sessionGroupRef, {});
  }, // bindActiveSessionGroup()

  unbindActiveSessionGroup() {
    return unbindDataBaseStore({ store: this, property: 'activeSessionGroup' }, {});
  }, // unbindActiveSessionGroup()

  async bindGroupSessions() {
    if (isEmpty(this.activeSessionGroup)) {
      return;
    }

    const clientsStore = useClientsStore();

    if (isEmpty(clientsStore.clients)) {
      await clientsStore.initializeClients();
    }

    const clients = map(clientsStore.clients, 'id');

    const sessionIds = chain(this.activeSessionGroup)
      .get('groupParticipants', [])
      .filter((participant) => (includes(clients, participant.client)))
      .map('session')
      .value();

    if (isEmpty(sessionIds)) {
      return;
    }

    const sessionsDb = new SessionsDB();

    // Firebase throws an error if we use more than 5 session IDs for binding in this query,
    // so we split them into chunks
    return bindDataBaseStoreByArrayOperator(
      { store: this, property: 'groupSessions' },
      sessionsDb,
      { clauses: sessionIds },
      [
        ['clinic', '==', this.activeSessionGroup.clinic],
        ['status', '==', 'running'],
      ],
      5,
    );
  }, // bindGroupSessions()

  unbindGroupSessions() {
    return unbindDataBaseStore({ store: this, property: 'groupSessions' }, []);
  }, // unbindGroupSessions()

  /**
   * Create bind to an active video session in which the user is a member of
   */
  async bindVideoCallSessions() {
    const auth = useAuthStore();

    if (isEmpty(auth.userDetails)) {
      return;
    }
    const videoSessionsDb = new VideoSessionsDB();
    const ref = videoSessionsDb.getQuery([
      ['clientId', '==', auth.currentClient.id],
      ['clinic', '==', auth.currentClient.clinic],
      ['status', '==', 'active'],
      ['members', 'array-contains', auth.userDetails.userId],
    ]);

    return bindDataBaseStore({ store: this, property: 'activeVideoCallSessions' }, ref, []);
  }, // bindVideoCallSessions()

  unbindVideoCallSessions() {
    return unbindDataBaseStore({ store: this, property: 'activeVideoCallSessions' }, []);
  }, // unbindVideoCallSessions()

  /**
   * Fetch all sessions related to current client
   */
  async bindSessions() {
    const auth = useAuthStore();

    if (isEmpty(auth.currentClient)) {
      return null;
    }

    const sessionsDb = new SessionsDB();
    const ref = sessionsDb.getQuery([
      ['client', '==', auth.currentClient.id],
      ['status', 'in', ['scheduled', 'created', 'running']],
    ], 'sessionName');

    return bindDataBaseStore({
      store: this,
      property: 'scheduledSessions',
      action: 'setScheduledSessions',
    }, ref, []);
  }, // bindSessions()

  unbindSessions() {
    return unbindDataBaseStore({
      store: this,
      property: 'scheduledSessions',
      action: 'setScheduledSessions',
    }, []);
  }, // unbindSessions()

  /**
   * Fetch all sessions related to current client
   */
  async bindSupervisingSessions() {
    const auth = useAuthStore();

    if ((isEmpty(auth.currentClient))
      || (![
        userRoles.MANAGER,
        userRoles.ADMINISTRATOR,
        userRoles.SUPERVISOR,
        userRoles.SENIOR_THERAPIST,
      ].includes(auth.userDetails?.role))
    ) {
      return null;
    }

    const sessionsDb = new SessionsDB();
    const ref = sessionsDb.getQuery([
      ['client', '==', auth.currentClient.id],
      ['status', 'in', ['processing', 'done']],
      ['supervisedBy', '==', auth.userDetails?.userId],
    ]);

    return bindDataBaseStore({ store: this, property: 'supervisingSessions' }, ref, []);
  }, // bindSupervisingSessions()

  unbindSupervisingSessions() {
    return unbindDataBaseStore({ store: this, property: 'supervisingSessions' }, []);
  }, // unbindSupervisingSessions()

  addActiveSessionProgramModification({ programId, programModNotes }) {
    if (!this.activeSession || isEmpty(this.activeSessionPrograms)) {
      return;
    }
    const sessionProgram = find(
      this.activeSessionPrograms,
      (activeSessionProgram) => activeSessionProgram?.program?.id === programId,
    );
    if (!sessionProgram) {
      return;
    }
    this.addActiveSessionProgramMod({ sessionProgram, programModNotes });
  }, // addActiveSessionProgramModification()

  async prepareSessionProgram({ clientId, programId, sessionProgram, allowLoadProgram }) {
    const programsTargets = useProgramsTargetsStore();
    const programs = useProgramsStore();
    const auth = useAuthStore();

    let program;

    if (clientId === auth.currentClient.id) {
      program = find(programs.programs, { id: programId });
    } else {
      program = chain(programs.cachedGroupPrograms)
        .get(clientId)
        .find({ id: programId })
        .value();
    }

    if (!program && allowLoadProgram) {
      const programsDb = new ProgramsDB();
      program = await programsDb.read(programId);
    }

    if (!program || program.isDeleted) {
      return null;
    }

    let programTargets = [];
    if (['taskAnalysis', 'sbt', 'set', 'ptp'].includes(program.type)) {
      programTargets = await programsTargets.getAllProgramTargets({ program: programId });
    } else {
      programTargets = await programsTargets.getActiveProgramTargets({ program: programId });
    }

    await programs.setProgramTarget({ program, programTargets });

    return {
      ...sessionProgram,
      program,
      programName: get(program, 'programName', sessionProgram.programName),
    };
  }, // prepareSessionProgram()

  /**
   * Load session programs, related programs and program details
   * @todo This is such a mess with the program prop being overwritten by a
   * program object... and targets being expected to be loaded into the program
   */
  async loadSessionPrograms({ session, allowLoadProgram = false }) {
    if (!session || !session.id) {
      return [];
    }
    const sessionProgramsDb = new SessionProgramsDB(session.id);
    const shuffle = get(session, 'shuffle', false);
    const sessionPrograms = await sessionProgramsDb.readOrdered(shuffle ? 'shuffleOrder' : 'order');

    // Add programs and targets
    const sessionProgramsActions = map(
      sessionPrograms,
      async (sessionProgram) => this.prepareSessionProgram({
        clientId: session.client,
        programId: sessionProgram.program,
        sessionProgram,
        allowLoadProgram,
      }),
    );

    return compact(await Promise.all(sessionProgramsActions));
  }, // loadSessionPrograms()

  async refreshSessionProgramPrograms({ programIds }) {
    if (!this.activeSession?.id) {
      return;
    }

    const sessionProgramActions = map(
      this.loadedActiveSessionPrograms,
      async (sessionProgram) => {
        const programId = sessionProgram?.program?.id;
        if (!includes(programIds, programId)) {
          return sessionProgram;
        }

        return this.prepareSessionProgram({
          clientId: this.activeSession.client,
          programId: sessionProgram.program.id,
          sessionProgram,
          allowLoadProgram: true,
        });
      },
    );

    const sessionPrograms = compact(await Promise.all(sessionProgramActions));
    this.setLoadedActiveSessionPrograms(sessionPrograms);
  }, // refreshSessionProgramPrograms()

  /**
   * Load active session, related programs and program details
   */
  async loadActiveSession({ sessionId, forceUpdate = false, loadGroupSessions = false }) {
    if ((get(this.activeSession, 'id') === sessionId) && !forceUpdate) {
      if (loadGroupSessions && isEmpty(this.groupSessions)) {
        const sessionGroupId = this.activeSession.sessionGroup || null;
        if (sessionGroupId !== null) {
          await this.bindActiveSessionGroup({ sessionGroupId });
          await this.bindGroupSessions();
        }
      }
      return;
    }
    const session = await this.bindActiveSession({ sessionId });
    const sessionPrograms = await this.loadSessionPrograms({ session });
    this.setLoadedActiveSessionPrograms(sessionPrograms);

    if (loadGroupSessions) {
      const sessionGroupId = get(this.activeSession, 'sessionGroup', null);
      if (sessionGroupId !== null) {
        await this.bindActiveSessionGroup({ sessionGroupId });
        await this.bindGroupSessions();
      }
    }
  }, // loadActiveSession()

  async switchActiveSession({ sessionId }) {
    if (get(this.activeSession, 'id') === sessionId) {
      return;
    }

    let session = null;

    if (get(this.primaryActiveSession, 'id') === sessionId) {
      session = this.primaryActiveSession;
      this.setActiveSessionIdPointer(null);
    } else {
      session = find(this.groupSessions, { id: sessionId }) || null;
      if (session === null) {
        return false;
      }
      this.setActiveSessionIdPointer(session.id);
    }
    await this.switchActiveSessionPrograms({ session });
    return true;
  }, // switchActiveSession()

  async switchActiveSessionPrograms({ session }) {
    let sessionPrograms = get(this.groupSessionPrograms, session.id, []);
    if (isEmpty(sessionPrograms)) {
      sessionPrograms = await this.loadSessionPrograms({ session });
      this.setGroupSessionPrograms({ sessionId: session.id, sessionPrograms });
    }

    this.setLoadedActiveSessionPrograms(sessionPrograms);
  }, // switchActiveSessionPrograms()

  /** Finish the active session and clean up */
  async finishActiveSession() {
    await Promise.all([
      this.unbindGroupSessions(),
      this.unbindActiveSessionGroup(),
      this.unbindActiveSession(),
    ]);
    this.setActiveSessionIdPointer(null);
    this.setLoadedActiveSessionPrograms([]);
  }, // finishActiveSession()

  /**
   * Cancel and finish the active session. Deletes session and session programs if
   * the session was ad hoc and not started yet */
  async cancelActiveSession() {
    if (['created', 'preparing'].includes(this.activeSession.status)) {
      const sessionsDb = new SessionsDB();
      await sessionsDb.deleteSession(this.activeSession.id);
    }
    await this.finishActiveSession();
  }, // cancelActiveSession()

  /**
   * Duplicate a session and change specified props for session
   * and sessionPrograms. Returns new session id
   * @param sessionId
   * @param {*} sessionProps
   * @param {*} sessionProgramProps
   * @param sessionProgramMappedProps
   * @param rejectInactivePrograms
   */
  async duplicateSession({
    sessionId,
    sessionProps = {},
    sessionProgramProps = {},
    sessionProgramMappedProps = [],
    rejectInactivePrograms = true,
  }) {
    const sessionsDb = new SessionsDB();
    let session = await sessionsDb.read(sessionId);
    // Reset session id and info according to supplied parameters
    session = {
      ...omit(session, 'sessionHistory'),
      id: null,
      ...sessionProps,
      isDeleted: false,
    };
    // Add program to store
    const newSession = await sessionsDb.create(session);

    const sessionProgramsDb = new SessionProgramsDB(sessionId);
    const sessionPrograms = await sessionProgramsDb.readAll();
    // Duplicate all active child sessionPrograms
    sessionProgramsDb.setSessionId(newSession.id);
    const createActions = chain(sessionPrograms)
      .map(((sessionProgram) => {
        const mappedProps = get(sessionProgramMappedProps, sessionProgram.id, {});

        const programs = useProgramsStore();

        const program = chain(programs)
          .get('programs')
          .find({ id: sessionProgram.program })
          .value();

        const sessionProgramsObj = {
          ...sessionProgram,
          id: null,
          ...sessionProgramProps,
          ...mappedProps,
        };

        if (rejectInactivePrograms
          && !ACTIVE_PROGRAM_STATUSES.includes(get(program, 'status'))) {
          return null;
        }

        return sessionProgramsDb.create(sessionProgramsObj);
      }))
      .reject((promise) => promise === null)
      .value();
    await Promise.all(createActions);

    return newSession.id;
  }, // duplicateSession()

  /**
   * Delete a session and underlying sessionPrograms and data
   */
  async deleteSession(id) {
    const sessionsDb = new SessionsDB();
    await sessionsDb.deleteSession(id);
  }, // deleteSession()

  updateTargetProperties({ sessionProgram, target, targetProperties }) {
    const programId = chain(this.loadedActiveSessionPrograms)
      .find({ id: sessionProgram.id })
      .get(['program', 'id'])
      .value();

    if (!programId) {
      return;
    }
    this.updateTargetPropertiesInTarget({ sessionProgram, target, targetProperties });

    const programsDb = new ProgramsDB();

    const programTargetsDb = new ProgramTargetsDB(programId);
    return Promise.all([
      programTargetsDb.set(targetProperties, target.id),
      programsDb.set({ }, programId), // Touch the program to update the last modified date
    ]);
  }, // updateTargetProperties()

  logout() {
    /*
    * todo [vuefire-migration] Should we reset the ActiveSession after unbind?
    *  There was the {reset: false} setting enabled on binding before migration.
    */
    return Promise.all([
      this.finishActiveSession(),
      this.unbindVideoCallSessions(),
      this.unbindSessions(),
      this.unbindSupervisingSessions(),
    ]);
  }, // logout()

  async loadVideoSessionMembersNames() {
    if (!this.currentVideoCallSession) {
      this.setVideoSessionMembersNames([]);
    }

    const { data } = await callFirebaseFunction('getVideoSessionMembers', {
      videoSessionId: this.currentVideoCallSession.id,
    });

    this.setVideoSessionMembersNames(data);

    return data;
  }, // loadVideoSessionMembersNames()

  /**
   * Fetch history sessions
   */
  async bindHistorySessions({ fromTime, toTime }) {
    const auth = useAuthStore();

    if (isEmpty(auth.currentClient)) {
      return;
    }

    const constraints = [
      ['client', '==', auth.currentClient.id],
      ['status', 'in', ['done', 'processing', 'offline-done']],
    ];

    if (fromTime) {
      constraints.push(['runTimestamp', '>=', fromTime]);
    }

    if (toTime) {
      constraints.push(['runTimestamp', '<=', toTime]);
    }

    const sessionsDb = new SessionsDB();
    const ref = sessionsDb.getQuery(constraints, 'runTimestamp', 'desc', 250);

    return bindDataBaseStore({ store: this, property: 'sessionsHistory' }, ref);
  }, // bindHistorySessions()

  async unbindHistorySessions() {
    return unbindDataBaseStore({ store: this, property: 'sessionsHistory' }, null, false);
  }, // unbindHistorySessions()

  bindIncidentHistory({ fromTime, toTime }) {
    const auth = useAuthStore();

    if (isEmpty(auth.currentClient)) {
      return;
    }

    const constraints = [
      ['client', '==', auth.currentClient.id],
      ['dataType', '==', 'Behaviour1.0'],
    ];

    if (fromTime) {
      constraints.push(['startTime', '>=', fromTime]);
    }

    if (toTime) {
      constraints.push(['startTime', '<=', toTime]);
    }

    const dataDb = new DataDB();
    const ref = dataDb.getQuery(constraints, 'startTime', 'desc', 250);

    return bindDataBaseStore({ store: this, property: 'incidentHistory' }, ref);
  }, // bindIncidentHistory()

  unbindIncidentHistory() {
    return unbindDataBaseStore({ store: this, property: 'incidentHistory' }, null, false);
  }, // unbindIncidentHistory()

  setHistoryBoundStatus(bound = true) {
    this.historyBound = bound;
  }, // setHistoryBoundStatus()

  clearGroupSessionPrograms({ sessionId }) {
    this.setGroupSessionPrograms({ sessionId, sessionPrograms: [] });
  },

  async getActiveVideoSession() {
    const auth = useAuthStore();

    if (isEmpty(auth.userDetails) || !this.activeSession?.id) {
      return;
    }

    const videoSessionsDb = new VideoSessionsDB();
    const activeVideoCallSessions = await videoSessionsDb.readAll([
      ['clientId', '==', auth.currentClient.id],
      ['clinic', '==', auth.currentClient.clinic],
      ['status', '==', 'active'],
      ['members', 'array-contains', auth.userDetails.userId],
    ]);

    return activeVideoCallSessions
      ?.sort((a, b) => (a?.createTimestamp?.toDate().getTime() ?? 0) - (b?.createTimestamp?.toDate().getTime() ?? 0))
      .find(({ sessionId }) => sessionId === this.activeSession.id);
  },

  setIsTrainingVideoPlaying(isPlaying) {
    this.isTrainingVideoPlaying = isPlaying;
  }, // setIsTrainingVideoPlaying()

  async isRunByInGroupSession() {
    const sessionId = this.activeSession?.id ?? null;

    const auth = useAuthStore();
    const sessionsDb = new SessionsDB();

    let currentSession = null;
    try {
      currentSession = await sessionsDb.read(sessionId);
    } catch (err) {
      logEvent(err);
    }

    if (currentSession?.runBy === auth.userDetails.userId) {
      return true;
    }

    notifyNegative(currentSession?.runByName
      ? i18n.t('store.sessionControlTransferred', { userName: currentSession.runByName })
      : i18n.t('store.sessionControlSyncError'));

    return false;
  }, // isRunByInGroupSession()
};
